import { FolderId, TDOId, TreeObjectId } from '@cbsa/state/modules/universal';
import { sagaIntl } from '@common/i18n';
import { notUndefined } from '@common/shared/util';
import { enqueueSnackbar } from '@common/state/modules/snackbar';
import {
  actionFetchFolderRequest,
  actionSearchNameExistsInFolderRequest,
  DELETE_FOLDER_FAILURE,
  DELETE_FOLDER_REQUEST,
  DELETE_FOLDER_SUCCESS,
  FETCH_ROOT_FOLDERS_FAILURE,
  FETCH_ROOT_FOLDERS_SUCCESS,
  MOVE_FOLDER_FAILURE,
  MOVE_FOLDER_SUCCESS,
  SEARCH_NAME_EXISTS_IN_FOLDER_FAILURE,
  SEARCH_NAME_EXISTS_IN_FOLDER_SUCCESS,
} from '@redact-modules/casemedia/actions';
import {
  ActionDeleteFolderPayload,
  SearchNameActions,
} from '@redact-modules/casemedia/models';
import {
  selectDefaultLimit,
  selectDefaultOffset,
  selectRootFolderId,
} from '@redact-modules/casemedia/selectors';
import { CreateNewFolderQueryResponse } from '@redact-modules/casemedia/services/queries';
import {
  getStampedTrashRetentionDays,
  isDateEligibleToDelete,
  selectAdminRootFolder,
} from '@redact-modules/helper';
import {
  actionMarkHomeListStale,
  DELETE_TDO_BULK_FAILURE,
  DELETE_TDO_BULK_SUCCESS,
} from '@redact-modules/mainPage';
import { deleteTDOBulk } from '@redact-modules/mainPage/services';
import { get, includes, without } from 'lodash';
import { purgeTdoSideData } from './purgeTdoSideData';
import { claimPurgeableTdos } from '../pendingPurge';
import {
  all,
  call,
  fork,
  put,
  putResolve,
  race,
  SagaGenerator,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'typed-redux-saga/macro';
import * as Actions from '../actions';
import { isTrashCleanupDue, markTrashCleanupRan } from '../cleanupThrottle';
import {
  RUN_CLEANUP_ON_TRASH_VISIT,
  SYSTEM_FOLDER_REDACT_TRASH,
  TRASH_CLEANUP_SCAN_LIMIT,
} from '../constants';
import {
  pickTrashContentTemplate,
  TrashContentTemplate,
  TrashFolder,
  TrashTDO,
} from '../models';
import {
  selectFilePageIndex,
  selectFilePageSize,
  selectFileSelectedItems,
  selectFolderPageIndex,
  selectFolderPageSize,
  selectFolderSelectedItems,
  selectSelectedFolderRows,
  selectSelectedTdoRows,
} from '../selectors';
import {
  checkExistingTrashFolder,
  createTrashFolder,
  deleteCaseSDOBulk,
  DeleteCaseSDOBulkResponse,
  deleteContentTemplateBulk,
  DeleteContentTemplateBulkResponse,
  ensureFolderParentPath,
  fetchCaseChildFiles,
  fetchSDOsBulkByTDOIds,
  fetchSoftDeletedFolders,
  FetchSoftDeletedFoldersResponse,
  fetchSoftDeletedTDOs,
  FetchSoftDeletedTDOsResponse,
  fetchTrashFolderState,
  restoreSoftDeletedSdos,
  restoreSoftDeletedTdos,
  RestoreTdosResult,
  TrashFolderState,
} from '../services';

export function* watchTrashTDOs() {
  yield* all([
    takeLatest(Actions.FETCH_TRASH_TDOS, fetchTrashTDOs),
    takeEvery(Actions.FETCH_TRASH_TDOS_FAILURE, fetchTrashTDOsFailure),
    takeLatest(Actions.FETCH_TRASH_TDOS_SUCCESS, handleTrashTDOsFetched),
  ]);
}

export function* fetchTrashTDOs(
  action: ReturnType<typeof Actions.FETCH_TRASH_TDOS>
) {
  const { limit, offset } = action.payload;
  const trashFolderId = yield* call(ensureTrashFolderId);

  if (trashFolderId) {
    yield* put(
      fetchSoftDeletedTDOs({
        trashFolderId,
        limit,
        offset,
      })
    );
  }
}

export function* fetchTrashTDOsFailure({
  payload,
}: {
  payload: {
    readonly name: string;
    readonly data: { readonly objectId: string };
  }[];
}) {
  const intl = sagaIntl();
  console.error('Error while fetching TDOs from trash page', payload);
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'errorLoadingTrashMedia',
        defaultMessage: 'An error occurred while loading the trash media list.',
      }),
    })
  );
}

export function* watchPermanentDeleteActions() {
  yield* all([
    takeEvery(Actions.PERMANENT_DELETE_TDOS, permanentDeleteTDOs),
    takeEvery(Actions.PERMANENT_DELETE_FOLDERS, permanentDeleteFolders),
    // VE-28551: deleting a request deletes the files inside it, and that
    // happens in a service thunk which cannot call a saga. It dispatches this
    // with the ids the SERVER confirmed it deleted, having registered them
    // first — this purges only what it can claim back, so the action carries no
    // authority of its own. See trash/pendingPurge.
    takeEvery(Actions.PURGE_TDO_SIDE_DATA, function* ({ payload }) {
      const claimed = claimPurgeableTdos(payload.tdoIds.map(String));
      if (claimed.length === 0) {
        console.warn(
          'trash: purge dropped — no registered delete for these ids',
          { requested: payload.tdoIds.length }
        );
        return;
      }
      yield* call(purgeTdoSideData, claimed as unknown as TDOId[]);
    }),
  ]);
}

export function* permanentDeleteTDOs() {
  const tdoIds = yield* select(selectFileSelectedItems);

  if (tdoIds.length === 0) {
    return;
  }
  const tdoIdsDeleted = yield* deleteTDOIdsPermanently(tdoIds);
  if (tdoIdsDeleted.length === 0) {
    return;
  }

  if (tdoIdsDeleted.length === tdoIds.length) {
    yield* put(
      enqueueSnackbar({
        message: sagaIntl().formatMessage({
          id: 'trashFilesDeleted',
          defaultMessage: 'Files deletion successful.',
        }),
      })
    );
  }

  // Drop only what was actually deleted: a file whose delete failed is still in
  // Trash, so keep it ticked and let a retry target just those (mirrors the
  // restore and permanent-delete-folders paths).
  yield* deselectFiles(tdoIdsDeleted);
  yield* put(Actions.UPDATE_TRASH_FILE_DATA_STORE({ tdoIds: tdoIdsDeleted }));
  yield* refillTrashFilesAfterMutation(tdoIdsDeleted.length);
}

// A TDO is eligible for permanent deletion once `trashRetentionDays` days have
// elapsed since `movedToTrashDateTime` (both stored on the first details tag;
// pre-rename items stamped `numberOfDaysToKeep` instead).
export const getExpiredTdoIds = (records: TrashTDO[]): TDOId[] =>
  records
    .filter((tdo) => {
      const firstTag = tdo.details?.tags?.[0];
      if (!firstTag || typeof firstTag !== 'object') {
        return false;
      }

      const movedToTrashDateTime =
        'movedToTrashDateTime' in firstTag
          ? firstTag.movedToTrashDateTime
          : undefined;
      const trashRetentionDays = getStampedTrashRetentionDays(firstTag) ?? 0;

      return (
        typeof movedToTrashDateTime === 'string' &&
        isDateEligibleToDelete(movedToTrashDateTime, trashRetentionDays)
      );
    })
    .map((tdo) => tdo.id);

function* handleTrashTDOsFetched({
  payload,
}: ReturnType<typeof Actions.FETCH_TRASH_TDOS_SUCCESS>) {
  const pageSize = yield* select(selectFilePageSize);
  const rawRecords = payload.folder?.childTDOs?.records || [];
  // N+1 lookahead: the fetch requested pageSize + 1 rows. Getting a full extra
  // row back means a next page exists; only the first pageSize rows belong to
  // this page. This drives the pagination footer without needing a (server-
  // unavailable) exact total count.
  const hasNextPage = rawRecords.length > pageSize;
  const pageRecords = pageSize > 0 ? rawRecords.slice(0, pageSize) : rawRecords;

  // Never display items past their permanent-delete deadline; show the rest
  // immediately so the user doesn't wait on the async purge. Expired filtering
  // only affects the displayed rows — hasNextPage is derived from the raw fetch
  // so a page heavy with expired items can never hide a reachable next page.
  const expiredTdoIds = getExpiredTdoIds(pageRecords);
  const expired = new Set(expiredTdoIds);
  const visibleRecords = pageRecords.filter((tdo) => !expired.has(tdo.id));

  yield* put(
    Actions.SET_TRASH_TDOS_DATA({
      ...payload,
      folder: {
        ...payload.folder,
        childTDOs: {
          ...payload.folder.childTDOs,
          records: visibleRecords,
        },
        hasNextPage,
      },
    })
  );

  // Fire-and-forget purge of the current page's expired items. Gated so the
  // on-visit purge can be turned off independently of the login-driven cleanup.
  // Items that fail to delete stay on the server (still hidden here) and are
  // retried on the next visit / login cleanup.
  if (expiredTdoIds.length > 0 && RUN_CLEANUP_ON_TRASH_VISIT) {
    yield* fork(deleteTDOIdsPermanently, expiredTdoIds);
  }
}

function* deleteTDOIdsPermanently(tdoIds: TDOId[]) {
  yield* put(
    deleteTDOBulk({
      tdoIds,
    })
  );

  const { err } = yield* race({
    ok: take(DELETE_TDO_BULK_SUCCESS),
    err: take(DELETE_TDO_BULK_FAILURE),
  });

  let tdoIdsDeleted: TDOId[];
  if (err) {
    const failedTDOIds = err.payload.map((error) =>
      get(error, 'data.objectId')
    );
    if (includes(failedTDOIds, undefined)) {
      // edge case: unable to determine which TDOs were deleted if any errors are missing objectId
      // may want to force a harder refresh for orphan SDO cleanup to kick in
      tdoIdsDeleted = [];
    } else {
      tdoIdsDeleted = without(tdoIds, ...failedTDOIds);
    }
  } else {
    tdoIdsDeleted = tdoIds;
  }
  if (tdoIdsDeleted.length === 0) {
    return [];
  }

  // Shared with the immediate-delete path, which used to clean up nothing.
  yield* call(purgeTdoSideData, tdoIdsDeleted);

  return tdoIdsDeleted;
}

// Drop `ids` from the selection, keeping whatever else is still ticked (e.g.
// an item that failed), so a retry acts on exactly what is left.
function* deselectFiles(ids: readonly TDOId[]) {
  const done = new Set<string>(ids);
  const selected = yield* select(selectFileSelectedItems);
  yield* put(
    Actions.UPDATE_SELECTED_FILE_ITEMS({
      tdoIds: selected.filter((id) => !done.has(id)),
    })
  );
}

function* deselectFolders(ids: readonly TreeObjectId[]) {
  const done = new Set<string>(ids);
  const selected = yield* select(selectFolderSelectedItems);
  yield* put(
    Actions.UPDATE_SELECTED_FOLDER_ITEMS({
      treeObjectId: selected.filter((id) => !done.has(id)),
    })
  );
}

// Folders the live re-read found no longer in Trash (see TrashFolderState).
function* warnFoldersNoLongerInTrash(count: number) {
  if (count === 0) {
    return;
  }
  yield* put(
    enqueueSnackbar({
      message: sagaIntl().formatMessage(
        {
          id: 'trashFoldersNoLongerInTrash',
          defaultMessage:
            '{count, plural, one {# folder is no longer in Trash and was skipped.} other {# folders are no longer in Trash and were skipped.}}',
        },
        { count }
      ),
      variant: 'warning',
    })
  );
}

// Where `folder` lives right now. Never throws.
function* readTrashFolderState(
  folder: TrashFolder,
  trashFolderId: string
): SagaGenerator<TrashFolderState> {
  try {
    return (yield* putResolve(
      fetchTrashFolderState({ folderId: folder.id, trashFolderId }) as never
    )) as TrashFolderState;
  } catch {
    return { status: 'unconfirmed' };
  }
}

export function* permanentDeleteFolders() {
  const folderIds = yield* select(selectFolderSelectedItems);

  if (folderIds.length === 0) {
    return;
  }
  // Resolve the rows before the first await — see restoreTDOs.
  const folders = yield* select(selectSelectedFolderRows);
  const trashFolderId = yield* call(ensureTrashFolderId);
  if (!trashFolderId) {
    return;
  }
  // The rows may come from the selection snapshot of another page, which can be
  // stale, and the delete is irreversible: re-read each folder first.
  const { deletedFolderIds, goneFolderIds, unconfirmedFolderIds } =
    yield* deleteFolderIdsPermanently(folders, {
      confirmInTrashFolderId: trashFolderId,
    });

  const intl = sagaIntl();
  const failedCount =
    folders.length - deletedFolderIds.length - goneFolderIds.length;
  // A folder whose delete failed has already raised its own error snackbar
  // (casemedia's deleteFolderFailure). One that could not be re-read — or not
  // resolved at all — raises none, so say so rather than fail silently; and
  // never claim success while any selected folder is still in Trash.
  const unreportedCount =
    unconfirmedFolderIds.length + folders.filter((row) => !row).length;
  if (unreportedCount > 0) {
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage(
          {
            id: 'trashFoldersPartiallyDeleted',
            defaultMessage:
              'Deleted {count} of {total, plural, one {# folder} other {# folders}}. {failed} could not be deleted.',
          },
          {
            count: deletedFolderIds.length,
            total: folders.length,
            failed: failedCount,
          }
        ),
        variant: 'warning',
      })
    );
  } else if (failedCount === 0 && deletedFolderIds.length > 0) {
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'trashFoldersDeleted',
          defaultMessage: 'Folders deletion successful.',
        }),
      })
    );
  }
  yield* warnFoldersNoLongerInTrash(goneFolderIds.length);

  // Both have left Trash: the deleted folders and those found elsewhere.
  const removedIds = [...deletedFolderIds, ...goneFolderIds];
  if (removedIds.length === 0) {
    return;
  }
  yield* deselectFolders(removedIds);
  yield* put(
    Actions.UPDATE_TRASH_FOLDER_DATA_STORE({ treeObjectIds: removedIds })
  );
  yield* refillTrashFoldersAfterMutation(removedIds.length);
}

export function* watchRestoreActions() {
  yield* all([
    takeEvery(Actions.RESTORE_TRASH_TDOS, restoreTDOs),
    takeEvery(Actions.RESTORE_TRASH_FOLDERS, restoreFolders),
  ]);
}

export function* restoreTDOs() {
  const tdoIds = yield* select(selectFileSelectedItems);

  if (tdoIds.length > 0) {
    // Selection survives paging, so resolve through the selection snapshot —
    // the current page alone would silently drop files ticked on other pages.
    // Resolve before the first await: leaving Trash clears the snapshot, and a
    // user may navigate away while ensureTrashFolderId is in flight.
    const tdos = yield* select(selectSelectedTdoRows);
    const validTdos = tdos.filter(notUndefined);

    if (validTdos.length === 0) {
      yield* put(
        enqueueSnackbar({
          message: sagaIntl().formatMessage({
            id: 'trashNoFilesRestored',
            defaultMessage: 'None of the selected files could be restored.',
          }),
          variant: 'warning',
        })
      );
      return;
    }

    const trashFolderId = yield* call(ensureTrashFolderId);
    if (!trashFolderId) {
      return;
    }

    const result = (yield* putResolve(
      restoreSoftDeletedTdos({ tdos: validTdos, trashFolderId }) as never
    )) as RestoreTdosResult;
    // Out of Trash: restored, plus those moved back whose tag strip failed.
    const outOfTrashIds = [...result.restoredTdoIds, ...result.leftoverTdoIds];
    const failedCount = tdos.length - outOfTrashIds.length;
    const intl = sagaIntl();

    if (outOfTrashIds.length === 0) {
      console.error('trash: file restore failed', {
        failed: result.failedTdoIds.length,
      });
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage({
            id: 'trashRestoreFilesError',
            defaultMessage: 'An error occurred while restoring files.',
          }),
          variant: 'error',
        })
      );
      return;
    }

    // Never claim full success when some selected files were skipped: the user
    // would not recheck Trash and the skipped files would expire unnoticed.
    yield* put(
      enqueueSnackbar(
        failedCount > 0
          ? {
              message: intl.formatMessage(
                {
                  id: 'trashFilesPartiallyRestored',
                  defaultMessage:
                    'Restored {count} of {total, plural, one {# file} other {# files}}. {failed} could not be restored.',
                },
                {
                  count: outOfTrashIds.length,
                  total: tdos.length,
                  failed: failedCount,
                }
              ),
              variant: 'warning',
            }
          : {
              message: intl.formatMessage({
                id: 'trashFilesRestored',
                defaultMessage: 'Files restore successfully.',
              }),
            }
      )
    );

    // Back in their folder but still tagged as trashed: Home hides them.
    if (result.leftoverTdoIds.length > 0) {
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage(
            {
              id: 'trashFilesRestoredWithLeftovers',
              defaultMessage:
                '{count, plural, one {Some trash data could not be removed from # restored file, so it may not show up on Home.} other {Some trash data could not be removed from # restored files, so they may not show up on Home.}}',
            },
            { count: result.leftoverTdoIds.length }
          ),
          variant: 'warning',
        })
      );
    }

    // Keep the files that failed selected, so a retry targets just those.
    yield* deselectFiles(outOfTrashIds);
    yield* put(Actions.UPDATE_TRASH_FILE_DATA_STORE({ tdoIds: outOfTrashIds }));
    // VE-26729: restored files belong back on Home, which this page can't update
    // itself. The nav drawer used to hard-reload the browser, so Home always came
    // back fresh; now it navigates in-app, so ask for the refresh explicitly.
    yield* put(actionMarkHomeListStale());
    // VE-24853: refetch the current page so it refills from any later items,
    // mirroring the permanent-delete path — otherwise restoring just shrinks
    // the visible list even when more trashed files exist.
    yield* refillTrashFilesAfterMutation(outOfTrashIds.length);
  }
}

export function* restoreFolders() {
  // Resolve every folder up front, not lazily inside the loop: each restore
  // takes several round trips, and leaving Trash meanwhile clears the snapshot
  // the later folders would otherwise be read from.
  const folders = yield* select(selectSelectedFolderRows);
  const restoredIds: TreeObjectId[] = [];
  const goneIds: TreeObjectId[] = [];
  let cleanupFailedCount = 0;

  let trashFolderId: string | undefined;
  if (folders.some(notUndefined)) {
    trashFolderId = yield* call(ensureTrashFolderId);
    if (!trashFolderId) {
      return;
    }
  }
  for (const folder of folders) {
    if (!folder || !trashFolderId) {
      continue;
    }
    const result = yield* restoreFolder(folder, trashFolderId);
    if (result === 'restored' || result === 'restoredWithLeftovers') {
      restoredIds.push(folder.treeObjectId);
    } else if (result === 'gone') {
      goneIds.push(folder.treeObjectId);
    }
    if (result === 'restoredWithLeftovers') {
      cleanupFailedCount += 1;
    }
  }

  yield* warnFoldersNoLongerInTrash(goneIds.length);
  // Folders found elsewhere have left Trash too: drop them from the selection
  // and the page like the restored ones.
  const removedIds = [...restoredIds, ...goneIds];
  // See restoreTDOs: report partial restores instead of claiming success.
  const failedCount = folders.length - removedIds.length;

  if (restoredIds.length === 0) {
    if (failedCount > 0) {
      yield* put(
        enqueueSnackbar({
          message: sagaIntl().formatMessage({
            id: 'trashNoFoldersRestored',
            defaultMessage: 'No valid folders to restore.',
          }),
          variant: 'warning',
        })
      );
    }
    if (goneIds.length > 0) {
      yield* deselectFolders(goneIds);
      yield* put(
        Actions.UPDATE_TRASH_FOLDER_DATA_STORE({ treeObjectIds: goneIds })
      );
      yield* refillTrashFoldersAfterMutation(goneIds.length);
    }
    return;
  }

  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar(
      failedCount > 0
        ? {
            message: intl.formatMessage(
              {
                id: 'trashFoldersPartiallyRestored',
                defaultMessage:
                  'Restored {count} of {total, plural, one {# folder} other {# folders}}. {failed} could not be restored.',
              },
              {
                count: restoredIds.length,
                total: folders.length,
                failed: failedCount,
              }
            ),
            variant: 'warning',
          }
        : {
            message: intl.formatMessage({
              id: 'trashFoldersRestored',
              defaultMessage: 'Folders restore successfully.',
            }),
          }
    )
  );

  // The folders are back in place, but some trash SDO / content-template links
  // survived. Not a failed restore — just don't let it go unnoticed.
  if (cleanupFailedCount > 0) {
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage(
          {
            id: 'trashFoldersRestoredWithLeftovers',
            defaultMessage:
              'Some trash data could not be removed from {count, plural, one {# restored folder} other {# restored folders}}.',
          },
          { count: cleanupFailedCount }
        ),
        variant: 'warning',
      })
    );
  }

  // Keep the folders that failed selected, so a retry targets just those.
  yield* deselectFolders(removedIds);
  yield* put(
    Actions.UPDATE_TRASH_FOLDER_DATA_STORE({ treeObjectIds: removedIds })
  );
  // VE-26729: restored folders belong back in Home's folder tree — see restoreTDOs.
  yield* put(actionMarkHomeListStale());
  // VE-24853: refill the current page after restore (mirrors permanent-delete).
  yield* refillTrashFoldersAfterMutation(removedIds.length);
}

// 'restoredWithLeftovers': the folder is back in place but its post-move
// content-template cleanup failed, so trash SDO links may remain on it.
// 'gone': the live folder is no longer in Trash; nothing was done to it.
type FolderRestoreResult =
  | 'restored'
  | 'restoredWithLeftovers'
  | 'failed'
  | 'gone';

function* restoreFolder(
  folder: TrashFolder,
  trashFolderId: string
): SagaGenerator<FolderRestoreResult> {
  // Restore from the live folder, not the selection snapshot (VE-29191): a
  // folder restored elsewhere meanwhile must not be moved back, and its stale
  // trash SDO restored in place would overwrite the live case SDO.
  const state = yield* call(readTrashFolderState, folder, trashFolderId);
  if (state.status === 'gone') {
    return 'gone';
  }
  if (state.status === 'unconfirmed') {
    return 'failed';
  }
  const templates = state.contentTemplates;
  const trashContentTemplate = pickTrashContentTemplate(templates);
  const oldFolderPath = trashContentTemplate?.sdo?.data?.oldFolderPath;
  if (!oldFolderPath || oldFolderPath.length < 2) {
    return 'failed';
  }

  // Ensure the parent path exists, recreating any missing intermediate folders.
  yield* put(
    ensureFolderParentPath({
      oldFolderPath,
      folderId: folder.treeObjectId,
    })
  );
  const { ok: ensureOk } = yield* race({
    ok: take(
      (action: any) =>
        action.type === Actions.ENSURE_FOLDER_PARENT_PATH_SUCCESS.type &&
        action.payload.folderId === folder.treeObjectId
    ),
    err: take(
      (action: any) =>
        action.type === Actions.ENSURE_FOLDER_PARENT_PATH_FAILURE.type &&
        action.payload.folderId === folder.treeObjectId
    ),
  });

  if (!ensureOk) {
    return 'failed';
  }

  const parentId = (
    ensureOk as unknown as ReturnType<
      typeof Actions.ENSURE_FOLDER_PARENT_PATH_SUCCESS
    >
  ).payload.parentId as FolderId;
  const searchPayload = {
    folderId: parentId,
    name: folder.name,
    actionName: SearchNameActions.RESTORE,
    actionPayload: {
      id: folder.treeObjectId,
      // VE-21114: the rename-on-collision branch authorizes updateFolder by
      // folder id under v2 (treeObjectId returns not_allowed).
      folderId: folder.id,
      name: folder.name,
      parentId,
    },
  };
  yield* put(actionSearchNameExistsInFolderRequest(searchPayload));
  const { ok: searchOk } = yield* race({
    ok: take(
      (action: any) =>
        action.type === SEARCH_NAME_EXISTS_IN_FOLDER_SUCCESS.type &&
        action.meta?.variables?.actionPayload?.id === folder.treeObjectId
    ),
    err: take(
      (action: any) =>
        action.type === SEARCH_NAME_EXISTS_IN_FOLDER_FAILURE.type &&
        action.meta?.variables?.actionPayload?.id === folder.treeObjectId
    ),
  });

  if (!searchOk) {
    return 'failed';
  }

  const { ok: moveOk, err: moveErr } = yield* race({
    ok: take(
      (action: any) =>
        action.type === MOVE_FOLDER_SUCCESS.type &&
        action.meta?.variables?.treeObjectId === folder.treeObjectId
    ),
    err: take(
      (action: any) =>
        action.type === MOVE_FOLDER_FAILURE.type &&
        action.meta?.variables?.treeObjectId === folder.treeObjectId
    ),
  });

  if (moveErr || !moveOk) {
    return 'failed';
  }

  if (templates.length === 0) {
    return 'restored';
  }

  if (trashContentTemplate?.sdo?.data?.isCase) {
    // trashContentTemplate is guaranteed defined here — the oldFolderPath guard
    // above returns early otherwise. Restore the trash SDO in place; buildSdoData
    // strips the trash fields so it becomes a clean case SDO again.
    const sdos = [
      { id: trashContentTemplate.sdoId, data: trashContentTemplate.sdo.data },
    ];

    yield* put(restoreSoftDeletedSdos({ sdos }));
    const { err: sdoErr } = yield* race({
      ok: take(Actions.RESTORE_SDOS_SUCCESS),
      err: take(Actions.RESTORE_SDOS_FAILURE),
    });

    if (sdoErr) {
      // The folder is already back under its parent, only its case SDO still
      // carries the trash fields. Count it as restored (so it leaves the Trash
      // list and its selection) and let the leftovers warning report it.
      console.error('Error while restoring requests', sdoErr.payload);
      return 'restoredWithLeftovers';
    }

    // Purge any *other* content templates so the restored case converges back to
    // a single SDO (a trashed case can carry a leftover case SDO of the same
    // schema — see pickTrashContentTemplate). Best-effort: don't fail the restore.
    const extras = templates.filter((t) => t.id !== trashContentTemplate.id);
    const cleaned = yield* cleanupContentTemplates(extras);

    return cleaned ? 'restored' : 'restoredWithLeftovers';
  }

  // Plain folder (or a folder mistakenly trashed via the folder path): drop every
  // attached SDO and unlink every content template so nothing orphans, even if
  // the folder accumulated more than one. The folder is already back in place by
  // now, so a cleanup failure must not count it as "could not be restored".
  const cleaned = yield* cleanupContentTemplates(templates);
  return cleaned ? 'restored' : 'restoredWithLeftovers';
}

/**
 * Unlinks the content templates and then deletes their SDOs for `templates`, in
 * that order. Returns false if either bulk mutation fails. Best-effort: callers
 * run it after the folder has already been moved out of Trash, so a failure is
 * reported as a restore with leftovers rather than a failed restore.
 *
 * Order is deliberate (VE-25907): unlink first, delete the SDO second. If the
 * unlink succeeds but the SDO delete then fails we are left with an orphaned SDO
 * (nothing references it — harmless and sweepable), never a dangling
 * content-template link (which blanks the trash table and strands the folder).
 * The reverse order is exactly what produced that bug.
 *
 * Uses `putResolve` to await each thunk's own result rather than racing on the
 * global DELETE_*_BULK_SUCCESS/FAILURE actions — those carry no correlation key,
 * so a concurrent purge (e.g. the login cleanup overlapping a trash-visit purge)
 * could otherwise consume the wrong completion.
 */
function* cleanupContentTemplates(
  templates: ReadonlyArray<TrashContentTemplate>
) {
  const contentTemplateIds = templates.map((t) => t.id).filter(Boolean);
  // Only delete SDOs that still exist. A template whose `sdo` resolved to null
  // points at an already-deleted SDO; asking the server to delete it again
  // answers `not_found`, which reads as failure (VE-25907). The link itself is
  // still real, so it stays in `contentTemplateIds` and gets unlinked below —
  // that's what clears the orphan.
  const sdoIds = templates
    .filter((t) => t.sdo)
    .map((t) => t.sdoId)
    .filter(Boolean);

  try {
    if (contentTemplateIds.length > 0) {
      // putResolve returns the thunk's resolved value at runtime (redux-thunk);
      // undefined / a throw means the request failed. typed-redux-saga only
      // types putResolve for plain actions, hence the cast.
      const res = (yield* putResolve(
        deleteContentTemplateBulk({ contentTemplateIds }) as never
      )) as DeleteContentTemplateBulkResponse | undefined;
      if (!res) {
        throw new Error('deleteContentTemplateBulk returned no data');
      }
    }

    if (sdoIds.length > 0) {
      const res = (yield* putResolve(
        deleteCaseSDOBulk({ sdoIds }) as never
      )) as DeleteCaseSDOBulkResponse | undefined;
      if (!res) {
        throw new Error('deleteCaseSDOBulk returned no data');
      }
    }
  } catch (err) {
    console.error('Error while cleaning up content templates', err);
    return false;
  }

  return true;
}

export function* watchTrashFolders() {
  yield* all([
    takeLatest(Actions.FETCH_TRASH_FOLDERS, fetchTrashFolders),
    takeEvery(Actions.FETCH_CASE_FILES_REQUEST, fetchCaseFilesRequest),
    takeEvery(Actions.FETCH_TRASH_FOLDERS_FAILURE, fetchTrashFoldersFailure),
    takeLatest(Actions.FETCH_TRASH_FOLDERS_SUCCESS, handleTrashFoldersFetched),
  ]);
}

export function* fetchTrashFolders(
  action: ReturnType<typeof Actions.FETCH_TRASH_FOLDERS>
) {
  const { limit, offset } = action.payload;
  const trashFolderId = yield* call(ensureTrashFolderId);

  if (trashFolderId) {
    yield* put(
      fetchSoftDeletedFolders({
        trashFolderId,
        limit,
        offset,
      })
    );
  }
}

export function* fetchCaseFilesRequest(
  action: ReturnType<typeof Actions.FETCH_CASE_FILES_REQUEST>
) {
  const { treeObjectId, limit, offset } = action.payload;
  yield* put(fetchCaseChildFiles({ treeObjectId, limit, offset }));
}

export function* fetchTrashFoldersFailure({
  payload,
}: {
  payload: {
    readonly name: string;
    readonly data: { readonly objectId: string };
  }[];
}) {
  const intl = sagaIntl();
  console.error('Error while fetching folders from trash page', payload);
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'errorLoadingTrashMedia',
        defaultMessage: 'An error occurred while loading the trash media list.',
      }),
    })
  );
}

// A trashed folder/case is eligible for permanent deletion once
// `trashRetentionDays` days have elapsed since `movedToTrashDateTime` (stored on
// its content-template SDO's data; pre-rename items stamped `numberOfDaysToKeep`
// instead).
export const getExpiredFolders = (records: TrashFolder[]): TrashFolder[] =>
  records.filter((folder) => {
    const data = pickTrashContentTemplate(folder.contentTemplates)?.sdo?.data;
    const movedToTrashDateTime = data?.movedToTrashDateTime;
    const trashRetentionDays = getStampedTrashRetentionDays(data) ?? 0;

    return (
      typeof movedToTrashDateTime === 'string' &&
      isDateEligibleToDelete(movedToTrashDateTime, trashRetentionDays)
    );
  });

function* handleTrashFoldersFetched({
  payload,
}: ReturnType<typeof Actions.FETCH_TRASH_FOLDERS_SUCCESS>) {
  const pageSize = yield* select(selectFolderPageSize);
  const rawRecords = payload.folder?.childFolders?.records || [];
  // N+1 lookahead — see handleTrashTDOsFetched.
  const hasNextPage = rawRecords.length > pageSize;
  const pageRecords = pageSize > 0 ? rawRecords.slice(0, pageSize) : rawRecords;

  // Never display items past their permanent-delete deadline; show the rest
  // immediately so the user doesn't wait on the async purge. hasNextPage is
  // derived from the raw fetch, so expired filtering can't hide a next page.
  const expiredFolders = getExpiredFolders(pageRecords);
  const expiredIds = new Set(expiredFolders.map((f) => f.treeObjectId));
  const visibleRecords = pageRecords.filter(
    (folder) => !expiredIds.has(folder.treeObjectId)
  );

  yield* put(
    Actions.SET_TRASH_FOLDERS_DATA({
      ...payload,
      folder: {
        ...payload.folder,
        childFolders: {
          ...payload.folder.childFolders,
          records: visibleRecords,
        },
        hasNextPage,
      },
    })
  );

  // Fire-and-forget purge (see handleTrashTDOsFetched); gated so the on-visit
  // purge can be turned off independently of the login-driven cleanup.
  if (expiredFolders.length > 0 && RUN_CLEANUP_ON_TRASH_VISIT) {
    yield* fork(deleteFolderIdsPermanently, expiredFolders, {
      suppressSnackbar: true,
    });
  }
}

function* deleteFolderIdsPermanently(
  folders: (TrashFolder | undefined)[],
  options?: {
    suppressSnackbar?: boolean;
    // Re-read each folder right before deleting it, and only delete it if it
    // is still directly under this Trash folder — for rows from the selection
    // snapshot, which can be stale (VE-29191). Its live content templates then
    // drive `isCase` and the orphaned-SDO purge. The background purges pass
    // rows they have just fetched, so they skip this.
    confirmInTrashFolderId?: string;
  }
) {
  const deletedFolderIds: TreeObjectId[] = [];
  // Confirmed no longer in Trash: skipped, and they should leave the list.
  const goneFolderIds: TreeObjectId[] = [];
  // Could not be re-read: left in Trash, and reported by the caller.
  const unconfirmedFolderIds: TreeObjectId[] = [];

  for (const folder of folders) {
    if (!folder) {
      continue;
    }

    let contentTemplates = folder.contentTemplates ?? [];
    if (options?.confirmInTrashFolderId) {
      const state = yield* call(
        readTrashFolderState,
        folder,
        options.confirmInTrashFolderId
      );
      if (state.status === 'gone') {
        goneFolderIds.push(folder.treeObjectId);
        continue;
      }
      if (state.status === 'unconfirmed') {
        // Can't confirm it is still in Trash: leave it for a retry.
        unconfirmedFolderIds.push(folder.treeObjectId);
        continue;
      }
      contentTemplates = state.contentTemplates;
    }
    const trashContentTemplate = pickTrashContentTemplate(contentTemplates);

    // Delete the folder tree object FIRST. deleteFolder removes the folder and
    // every content-template link with it, so no dangling reference can survive
    // — even if an SDO delete below fails. We dispatch DELETE_FOLDER_REQUEST
    // directly rather than via deleteCaseSdo (which deletes the trash SDO first
    // and only then the folder): that old order stranded a folder whenever its
    // trash SDO was already gone, because a `not_found` on the SDO read as
    // failure and the folder delete never ran (VE-25907). Deleting the folder
    // unconditionally also clears a folder whose own trash SDO is dangling.
    yield* put(
      DELETE_FOLDER_REQUEST({
        treeObjectId: folder.treeObjectId,
        // VE-21114: v2 deletes by folder id (deprecated treeObjectId returns
        // not_allowed under OLP). deleteFolderRequest swaps to this under v2.
        folderId: folder.id,
        folderName: folder.name,
        isPermanentDelete: true,
        suppressSnackbar: options?.suppressSnackbar,
        isCase: trashContentTemplate?.sdo?.data?.isCase,
      } as ActionDeleteFolderPayload)
    );

    const currentTreeObjectId = folder.treeObjectId;
    // The delete mutation is keyed by treeObjectId (v1) or folder id (v2); match
    // on either so the race resolves under both.
    const matchesFolder = (variables: { readonly id?: string } | undefined) =>
      variables?.id === currentTreeObjectId || variables?.id === folder.id;
    const { ok } = yield* race({
      ok: take(
        (action: any) =>
          action.type === DELETE_FOLDER_SUCCESS.type &&
          matchesFolder(action.meta?.variables)
      ),
      err: take(
        (action: any) =>
          action.type === DELETE_FOLDER_FAILURE.type &&
          matchesFolder(action.meta?.variables)
      ),
    });

    if (!ok) {
      // Folder delete failed — nothing was deleted, so nothing is orphaned or
      // dangling. Leave the folder in trash; the next cleanup pass retries.
      continue;
    }
    deletedFolderIds.push(folder.treeObjectId);

    // The folder and its content-template links are gone. Now best-effort purge
    // the SDOs it referenced (the trash SDO plus any extras), which are now
    // orphaned. A failure here leaves a harmless orphaned SDO, never a dangling
    // link, and the folder is already out of trash. Skip SDOs already known to
    // be gone (`sdo` resolved to null) so we don't ask to delete them twice.
    // putResolve (not a global take) avoids cross-talk with a concurrent purge.
    const orphanSdoIds = contentTemplates
      .filter((t) => t.sdo)
      .map((t) => t.sdoId)
      .filter(Boolean);
    if (orphanSdoIds.length > 0) {
      try {
        const res = (yield* putResolve(
          deleteCaseSDOBulk({ sdoIds: orphanSdoIds }) as never
        )) as DeleteCaseSDOBulkResponse | undefined;
        if (!res) {
          throw new Error('deleteCaseSDOBulk returned no data');
        }
      } catch (err) {
        console.error(
          'Folder permanently deleted, but orphaned SDO purge failed',
          folder.treeObjectId,
          err
        );
      }
    }
  }

  return { deletedFolderIds, goneFolderIds, unconfirmedFolderIds };
}

// Refetch the current file page after a mutation (permanent delete or restore)
// removed rows, so it refills from any later trashed items and hasNextPage is
// recomputed. No-op when nothing was mutated.
function* refillTrashFilesAfterMutation(mutatedCount: number) {
  if (mutatedCount <= 0) {
    return;
  }
  const pageIndex = yield* select(selectFilePageIndex);
  const pageSize = yield* select(selectFilePageSize);
  yield* put(
    Actions.actionFetchTrashTDOs({
      limit: pageSize,
      offset: pageIndex * pageSize,
    })
  );
  yield* take([
    Actions.FETCH_TRASH_TDOS_SUCCESS,
    Actions.FETCH_TRASH_TDOS_FAILURE,
  ]);
}

function* refillTrashFoldersAfterMutation(mutatedCount: number) {
  if (mutatedCount <= 0) {
    return;
  }
  const pageIndex = yield* select(selectFolderPageIndex);
  const pageSize = yield* select(selectFolderPageSize);
  yield* put(
    Actions.actionFetchTrashFolders({
      limit: pageSize,
      offset: pageIndex * pageSize,
    })
  );
  yield* take([
    Actions.FETCH_TRASH_FOLDERS_SUCCESS,
    Actions.FETCH_TRASH_FOLDERS_FAILURE,
  ]);
}

/**
 * Login-driven background purge of expired trash items. Throttled via
 * localStorage (see cleanupThrottle) so it runs at most once per interval, and
 * scans through a dedicated silent fetch path so it never disturbs the Trash
 * page display store. Runs independently of the Trash page, so expired items
 * are removed even if the user never opens Trash.
 *
 * Intended to be forked (not awaited) from the login handler so it never delays
 * the app. A later phase will additionally persist the last-run timestamp to an
 * org SDO (checked after localStorage) — see the trash-cleanup writeup.
 */
export function* runLoginTrashCleanup() {
  if (!isTrashCleanupDue()) {
    return;
  }

  const trashFolderId = yield* call(ensureTrashFolderId);
  if (!trashFolderId) {
    // App likely not ready yet; leave the throttle unset so the next login
    // retries rather than skipping cleanup for the whole interval.
    return;
  }

  try {
    // `putResolve` on a thunk returns its resolved value at runtime (redux-thunk
    // middleware), but typed-redux-saga only types it for plain actions — hence
    // the casts.
    const tdoResponse = (yield* putResolve(
      fetchSoftDeletedTDOs({
        trashFolderId,
        limit: TRASH_CLEANUP_SCAN_LIMIT,
        offset: 0,
        silent: true,
      }) as never
    )) as FetchSoftDeletedTDOsResponse | undefined;
    const expiredTdoIds = getExpiredTdoIds(
      tdoResponse?.folder?.childTDOs?.records || []
    );
    if (expiredTdoIds.length > 0) {
      yield* call(deleteTDOIdsPermanently, expiredTdoIds);
    }

    const folderResponse = (yield* putResolve(
      fetchSoftDeletedFolders({
        trashFolderId,
        limit: TRASH_CLEANUP_SCAN_LIMIT,
        offset: 0,
        silent: true,
      }) as never
    )) as FetchSoftDeletedFoldersResponse | undefined;
    const expiredFolders = getExpiredFolders(
      folderResponse?.folder?.childFolders?.records || []
    );
    if (expiredFolders.length > 0) {
      yield* call(deleteFolderIdsPermanently, expiredFolders, {
        suppressSnackbar: true,
      });
    }

    // Mark as run only when both scans actually returned data — a successful
    // scan with nothing expired still returns defined objects, whereas a failed
    // fetch resolves to undefined. Leaving the throttle unset on failure means
    // the next login retries rather than skipping cleanup for the interval.
    if (tdoResponse && folderResponse) {
      markTrashCleanupRan();
    }
  } catch (e) {
    // Leave the throttle unset so cleanup retries on the next login.
    console.error('Trash cleanup on login failed', e);
  }
}

// The root folder the folder view resolved — it runs the
// `rootFolderSmartSelection` heuristic over the full listRootFolders payload,
// which Trash's own query can't (its childFolders page is filtered to the
// REDACT TRASH folder, so every root reports 0 or 1 children). Nothing is
// resolved yet when the user lands on /trash directly, or during the login
// cleanup, so fetch the roots through casemedia's own request and let the same
// resolution run. Returns '' if that fetch fails; the caller then falls back to
// resolving a root from the trash payload itself.
export function* resolveFolderViewRootFolderId(): SagaGenerator<string> {
  const resolvedRootFolderId = yield* select(selectRootFolderId);
  if (resolvedRootFolderId) {
    return resolvedRootFolderId;
  }

  const limit = yield* select(selectDefaultLimit);
  const offset = yield* select(selectDefaultOffset);
  yield* put(actionFetchFolderRequest({ limit, offset }));
  const result = yield* race({
    ok: take(FETCH_ROOT_FOLDERS_SUCCESS.type),
    err: take(FETCH_ROOT_FOLDERS_FAILURE.type),
  });

  return result.ok ? yield* select(selectRootFolderId) : '';
}

export function* ensureTrashFolderId(): SagaGenerator<string | undefined> {
  const intl = sagaIntl();
  const resolvedRootFolderId = yield* call(resolveFolderViewRootFolderId);
  yield* put(checkExistingTrashFolder());
  const result = yield* race({
    ok: take(Actions.CHECK_EXISTING_TRASH_FOLDER_SUCCESS),
    err: take(Actions.CHECK_EXISTING_TRASH_FOLDER_FAILURE),
  });

  if (result.err) {
    console.error('Failed to load root folder data', result.err);
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'trashFolderRootLoadFailed',
          defaultMessage:
            'Failed to load root folder data for trash. Please try again.',
        }),
        variant: 'error',
      })
    );
    return undefined;
  }

  // VE-21114: resolve the same admin root the rest of the app uses (owner-less
  // root else [0]) so Trash lands under the same root under v2 (was [0]).
  //
  // Match the root the folder view resolved (see
  // resolveFolderViewRootFolderId): on an org with several owner-less roots,
  // re-deriving it from this payload would let Trash create REDACT TRASH under a
  // different root than the dashboard uses. Only if that resolution failed —
  // its fetch errored, or the org has no root at all — fall back to picking a
  // root from this payload, which is the pre-flag behaviour.
  const rootFolders = result.ok?.payload?.rootFolders;
  const rootFolder =
    rootFolders?.find((folder) => folder.id === resolvedRootFolderId) ??
    selectAdminRootFolder(rootFolders);

  if (!rootFolder?.id) {
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'trashFolderRootMissing',
          defaultMessage:
            'Missing root folder for trash. Please try again later.',
        }),
        variant: 'error',
      })
    );
    return undefined;
  }

  const existingTrashFolder = rootFolder.childFolders?.records?.[0];

  if (existingTrashFolder?.id) {
    return existingTrashFolder.id;
  }

  yield* put(
    createTrashFolder({
      newFolderName: SYSTEM_FOLDER_REDACT_TRASH,
      rootFolderId: rootFolder.id,
    })
  );

  const createResult: {
    type: string;
    payload?: CreateNewFolderQueryResponse;
  } = yield* take([
    Actions.CREATE_TRASH_FOLDER_SUCCESS.type,
    Actions.CREATE_TRASH_FOLDER_FAILURE.type,
  ]);

  if (
    createResult.type === Actions.CREATE_TRASH_FOLDER_FAILURE.type ||
    !createResult.payload
  ) {
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'trashFolderCreateFailed',
          defaultMessage: 'Failed to create trash folder. Please try again.',
        }),
        variant: 'error',
      })
    );
    return undefined;
  }

  return createResult.payload.createFolder.id;
}

export function* onActionFetchSDOsBulkByTDOIds() {
  yield* takeEvery(Actions.FETCH_SDOS_BULK_BY_TDO_IDS, function* ({ payload }) {
    yield* put(fetchSDOsBulkByTDOIds(payload));
  });
}
